import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Typography,
  Box,
  Paper,
  Link,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { login } from "../../service/authService";
import logo from "../../assets/logo.svg";
import AppButton from "../../components/AppButton";
import Color from "../../components/Color";
import { useSnackbar } from "../../App";
import Loader from "../../components/Loader";

const Login = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [rememberMeChecked, setRememberMeChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  // Responsive helpers
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Auto-fill saved email & password

  useEffect(() => {
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";
    const savedEmail = localStorage.getItem("savedEmail") || "";
    const savedPassword = localStorage.getItem("savedPassword") || "";

    setRememberMeChecked(savedRememberMe);

    if (savedRememberMe) {
      setEmail(savedEmail);
      setPassword(savedPassword);
    }
  }, []);

  // login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const payload = {
        email: email.trim(),
        password: password,
        remember: !!rememberMeChecked,
      };

      const res = await login(payload);
      const { access_token, user } = res.data;

      // Save tokens + user info
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("role", user.role_id);
      localStorage.setItem("name", user.name);
      localStorage.setItem("email", user.email);
      localStorage.setItem("branch", user.branch_id);

      if (rememberMeChecked) {
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("savedEmail", email);
        localStorage.setItem("savedPassword", password);
      } else {
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("savedEmail");
        localStorage.removeItem("savedPassword");
      }

      setTimeout(() => {
        setLoading(false);
        showSnackbar("Login successful!", "success");
        navigate("/dashboard");
      }, 700);
    } catch (err) {
      const backendError =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Login failed. Please try again.";

      showSnackbar(backendError, "error");
      setError(backendError);
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <Container
        maxWidth="xs"
        sx={{
          px: 2,
          display: "flex",
          minHeight: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            width: "100%",
            maxWidth: 500,
            boxSizing: "border-box",
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              height: isMobile ? 100 : 130,
              display: "block",
              mx: "auto",
              mb: 2,
              maxWidth: "100%",
            }}
          />

          {/* Title */}
          <Typography
            variant={isMobile ? "h6" : "h5"}
            align="center"
            gutterBottom
            color="#7C0BCC"
            fontWeight={600}
          >
            Login to Account
          </Typography>

          {/* Login Form */}
          <form onSubmit={handleLogin}>
            <Box display="flex" flexDirection="column" gap={2} mt={3}>
              {/* Email Field */}
              <Box>
                <Typography
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: "13px",
                  }}
                >
                  Email*
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                  required
                  autoComplete="email"
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    mb: 0,
                  }}
                />
              </Box>

              {/* Password Field */}
              <Box>
                <Typography
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: "13px",
                  }}
                >
                  Password*
                </Typography>
                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  autoComplete="current-password"
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    mb: 0,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Remember Me & Forgot Password */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={0}
                gap={isMobile ? 0.5 : 0}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMeChecked}
                      onChange={(e) => setRememberMeChecked(e.target.checked)}
                      color="primary"
                      size="small"
                      sx={{
                        "&.Mui-checked": {
                          color: Color.primary.main,
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: "20px",
                        },
                      }}
                    />
                  }
                  label="Remember me"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: {
                        xs: "12px",
                        sm: "14px",
                        md: "16px",
                      },
                    },
                  }}
                />

                <Link
                  href="/forgot"
                  variant="body2"
                  underline="hover"
                  color="#7C0BCC"
                  sx={{ mt: isMobile ? 1 : 0, fontSize: { xs: 12 } }}
                >
                  Forgot Password?
                </Link>
              </Box>

              {/* Submit Button */}
              <AppButton
                type="submit"
                color="primary"
                sx={{ mt: "10px", mb: "0px", height: "50px" }}
                disabled={loading}
                fullWidth
              >
                Login
              </AppButton>
            </Box>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default Login;
